import React, { useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, ArrowLeft, ShieldCheck, ClipboardCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Public: prospective Distributing Partner submits their application. Sits in
// `pending` until HQ reviews from /ep-applications. Mirrors the client-apply
// / cosmetology-license compliance pattern — driver's license + proof of
// insurance are both mandatory.
const EMPTY = {
  name: "",
  email: "",
  phone: "",
  address: "",
  driver_license_number: "",
  insurance_policy_number: "",
};

export default function PublicEpApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [dlFile, setDlFile] = useState(null);
  const [dlPreview, setDlPreview] = useState("");
  const [insFile, setInsFile] = useState(null);
  const [insPreview, setInsPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const dlRef = useRef(null);
  const insRef = useRef(null);

  const pickImage = (file, setFile, setPreview, label) => {
    if (!file) return;
    if (!/\.(jpe?g|png|gif|webp)$/i.test(file.name)) {
      toast.error(`${label} must be JPG, PNG, GIF, or WebP`);
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error(`${label} must be under 15 MB`);
      return;
    }
    setFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.name.trim()) missing.push("Full Name");
    if (!form.email.trim()) missing.push("Email");
    if (!form.phone.trim()) missing.push("Phone");
    if (!form.address.trim()) missing.push("Address");
    if (!form.driver_license_number.trim()) missing.push("Driver's License Number");
    if (!dlFile) missing.push("Driver's License Image");
    if (!form.insurance_policy_number.trim()) missing.push("Insurance Policy Number");
    if (!insFile) missing.push("Proof of Insurance Image");
    if (missing.length) {
      toast.error(`Please complete: ${missing.join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("phone", form.phone.trim());
      fd.append("address", form.address.trim());
      fd.append("driver_license_number", form.driver_license_number.trim());
      fd.append("insurance_policy_number", form.insurance_policy_number.trim());
      fd.append("driver_license_image", dlFile);
      fd.append("insurance_image", insFile);
      await axios.post(`${API}/api/ep-applications`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Application submitted — HQ will review shortly.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--bg-soft)] flex items-center justify-center px-4 py-10">
        <Card className="max-w-lg w-full p-8 text-center" data-testid="ep-apply-success">
          <CheckCircle2 size={44} className="mx-auto text-emerald-600 mb-3" />
          <h1 className="text-2xl font-bold mb-2">Application received</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Thanks for applying to become an MPIRE Distributing Partner. Our team will review your driver's
            license and proof of insurance and email you a decision at <b>{form.email}</b> within a few business days.
          </p>
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" onClick={() => navigate("/")}>
              <ArrowLeft size={14} className="mr-1" /> Back to home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-soft)]">
      <header className="bg-black text-white py-4 px-6 flex items-center gap-3">
        <Link to="/" className="text-sm inline-flex items-center gap-1 opacity-80 hover:opacity-100">
          <ArrowLeft size={14} /> Back
        </Link>
        <div className="flex-1 text-center font-bold tracking-widest">MPIRE Distribution</div>
        <div className="w-20" />
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="label-eyebrow text-[var(--brand)]">Become a Distributing Partner</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1">Apply to distribute for MPIRE</h1>
          <p className="text-sm text-gray-600 mt-2 leading-relaxed">
            Distributing Partners handle last-mile fulfillment for MPIRE clients in their territory.
            To keep our clients safe and insured, every DP must submit a valid driver's license and
            proof of active insurance for HQ review. Fields marked <b>*</b> are required.
          </p>
        </div>

        <Card className="p-6" data-testid="ep-apply-form">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="ep-apply-name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="ep-apply-email"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="ep-apply-phone"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Address *</Label>
              <Textarea
                required
                rows={2}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street, city, state, ZIP"
                data-testid="ep-apply-address"
              />
            </div>

            {/* Driver's License */}
            <div className="sm:col-span-2 rounded-lg border border-amber-300 bg-amber-50/50 p-4">
              <p className="label-eyebrow text-amber-800 flex items-center gap-1.5">
                <ShieldCheck size={12} /> Driver's License · Required
              </p>
              <p className="text-xs text-amber-900/80 mt-1 mb-3">
                Number and clear photo of your valid driver's license. Used only for HQ verification.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">License number *</Label>
                  <Input
                    required
                    value={form.driver_license_number}
                    onChange={(e) => setForm({ ...form, driver_license_number: e.target.value })}
                    placeholder="e.g. TX-DL-12345678"
                    data-testid="ep-apply-dl-number"
                  />
                </div>
                <div>
                  <Label className="text-xs">License photo *</Label>
                  <input
                    ref={dlRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => pickImage(e.target.files?.[0], setDlFile, setDlPreview, "Driver's license")}
                    data-testid="ep-apply-dl-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-1"
                    onClick={() => dlRef.current?.click()}
                    data-testid="ep-apply-dl-image-button"
                  >
                    <UploadCloud size={14} className="mr-1" /> {dlFile ? "Change file" : "Choose file"}
                  </Button>
                  {dlPreview && (
                    <img src={dlPreview} alt="Driver's license preview" className="mt-2 rounded max-h-32 border" />
                  )}
                </div>
              </div>
            </div>

            {/* Insurance */}
            <div className="sm:col-span-2 rounded-lg border border-emerald-300 bg-emerald-50/50 p-4">
              <p className="label-eyebrow text-emerald-800 flex items-center gap-1.5">
                <ClipboardCheck size={12} /> Proof of Insurance · Required
              </p>
              <p className="text-xs text-emerald-900/80 mt-1 mb-3">
                Active policy number and a scan/photo of your insurance card or declarations page.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Policy number *</Label>
                  <Input
                    required
                    value={form.insurance_policy_number}
                    onChange={(e) => setForm({ ...form, insurance_policy_number: e.target.value })}
                    placeholder="e.g. POL-778899"
                    data-testid="ep-apply-ins-number"
                  />
                </div>
                <div>
                  <Label className="text-xs">Policy document *</Label>
                  <input
                    ref={insRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => pickImage(e.target.files?.[0], setInsFile, setInsPreview, "Insurance document")}
                    data-testid="ep-apply-ins-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-1"
                    onClick={() => insRef.current?.click()}
                    data-testid="ep-apply-ins-image-button"
                  >
                    <UploadCloud size={14} className="mr-1" /> {insFile ? "Change file" : "Choose file"}
                  </Button>
                  {insPreview && (
                    <img src={insPreview} alt="Insurance preview" className="mt-2 rounded max-h-32 border" />
                  )}
                </div>
              </div>
            </div>

            <div className="sm:col-span-2 flex justify-end mt-2">
              <Button type="submit" className="btn-brand" disabled={submitting} data-testid="ep-apply-submit">
                {submitting ? "Submitting…" : "Submit application"}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
